import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckSquare } from "lucide-react";
import { formatApiError } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("This link is missing a token.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can log in now.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
      <Card className="w-full max-w-md rounded-md border-slate-200 shadow-sm">
        <CardHeader className="space-y-3">
          <div className="w-10 h-10 rounded-md bg-slate-900 text-white grid place-items-center">
            <CheckSquare size={18} />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
            Choose a new password
          </CardTitle>
          <p className="text-sm text-slate-500">Set a fresh password for your TaskFlow account. This link expires 1 hour after it was sent.</p>
        </CardHeader>
        <CardContent>
          {done ? (
            <p className="text-sm text-emerald-700">Password updated. Redirecting to log in…</p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw" autoFocus />
                <p className="text-xs text-slate-500">Minimum 6 characters.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw2">Confirm new password</Label>
                <Input id="pw2" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2" />
              </div>
              <Button type="submit" disabled={busy || !token} className="w-full" data-testid="reset-submit">
                {busy ? "Saving…" : "Update password"}
              </Button>
              <p className="text-center text-sm text-slate-500">
                <Link to="/login" className="text-slate-900 font-medium hover:underline">Back to login</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
